import type {
  GainersLosersQuery,
  LiveMarketQuery,
  StockPricesQuery,
  Week52Query,
} from '@/schemas/prices'

/**
 * Query key factory.
 *
 * A table keys on its whole query object, not on `page` alone. Page, page
 * size, sector, breadth and the search box all change which rows come back, so
 * anything short of the full object would serve page 3 of the unfiltered table
 * to a member who has just picked a sector. Object keys are hashed
 * structurally by TanStack Query, so `{ page: 1, pageSize: 10 }` built twice is
 * one cache entry.
 *
 * The group keys above each table exist for invalidation: a refresh on Live
 * market drops every page and every filter at once, which is the only correct
 * granularity when the underlying close has moved.
 */
export const pricesKeys = {
  all: ['prices'] as const,
  liveMarketTables: () => [...pricesKeys.all, 'live-market'] as const,
  liveMarket: (query: LiveMarketQuery) => [...pricesKeys.liveMarketTables(), query] as const,
  stockPriceTables: () => [...pricesKeys.all, 'stock-prices'] as const,
  stockPrices: (query: StockPricesQuery) => [...pricesKeys.stockPriceTables(), query] as const,
  week52Tables: () => [...pricesKeys.all, 'week-52'] as const,
  week52: (query: Week52Query) => [...pricesKeys.week52Tables(), query] as const,
  /** NEPSE's own 52-week row, pinned above the table (not part of any page of it). */
  week52Nepse: () => [...pricesKeys.all, 'week-52-nepse'] as const,
  gainersLosersTables: () => [...pricesKeys.all, 'gainers-losers'] as const,
  gainersLosers: (query: GainersLosersQuery) =>
    [...pricesKeys.gainersLosersTables(), query] as const,
  history: (symbol: string, range: '1W' | '1M' | '3M' | '1Y' | 'ALL') =>
    [...pricesKeys.all, 'history', symbol, range] as const,
}
